import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Plus, Search, Trash2, Edit, UserCog, Save, X, 
  ToggleLeft, ToggleRight, Users
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from '@/components/ui/badge';

const CARGOS = [
  'Enfermera',
  'Enfermero',
  'Técnico en Enfermería',
  'Matrona',
  'Médico',
  'Paramédico',
  'Auxiliar de Enfermería',
  'Otro'
];

const StaffPage = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  
  const [formData, setFormData] = useState({
    nombre_completo: '',
    rut: '',
    cargo: ''
  });

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/staff?active_only=${!showInactive}`, {
        withCredentials: true
      });
      setStaff(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
      toast.error('Error al cargar el personal');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const handleOpenDialog = (staffMember = null) => {
    if (staffMember) {
      setEditingStaff(staffMember);
      setFormData({
        nombre_completo: staffMember.nombre_completo,
        rut: staffMember.rut || '',
        cargo: staffMember.cargo
      });
    } else {
      setEditingStaff(null);
      setFormData({
        nombre_completo: '',
        rut: '',
        cargo: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingStaff(null);
    setFormData({
      nombre_completo: '',
      rut: '',
      cargo: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre_completo.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!formData.cargo) {
      toast.error('El cargo es requerido');
      return;
    }

    try {
      if (editingStaff) {
        await axios.put(`${API}/staff/${editingStaff.id}`, formData, {
          withCredentials: true
        });
        toast.success('Personal actualizado');
      } else {
        await axios.post(`${API}/staff`, formData, {
          withCredentials: true
        });
        toast.success('Personal agregado');
      }
      handleCloseDialog();
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await axios.delete(`${API}/staff/${deleteId}`, {
        withCredentials: true
      });
      toast.success('Personal desactivado');
      fetchStaff();
    } catch (error) {
      toast.error('Error al desactivar');
    } finally {
      setDeleteId(null);
    }
  };

  const handleToggleActive = async (staffMember) => {
    try {
      if (staffMember.active) {
        await axios.delete(`${API}/staff/${staffMember.id}`, {
          withCredentials: true
        });
        toast.success('Personal desactivado');
      } else {
        await axios.post(`${API}/staff/${staffMember.id}/activate`, {}, {
          withCredentials: true
        });
        toast.success('Personal activado');
      }
      fetchStaff();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const filteredStaff = staff.filter(s => 
    s.nombre_completo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.rut && s.rut.toLowerCase().includes(searchTerm.toLowerCase())) ||
    s.cargo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="staff-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 heading">Personal</h1>
            <p className="text-slate-500 mt-1">
              Gestión del personal que administra vacunas
            </p>
          </div>
          <Button 
            className="btn-accent gap-2" 
            onClick={() => handleOpenDialog()}
            data-testid="add-staff-btn"
          >
            <Plus className="w-4 h-4" />
            Agregar Personal
          </Button>
        </div>

        {/* Filters */}
        <Card data-testid="filters-card">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre, RUT o cargo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <Button
                variant={showInactive ? "default" : "outline"}
                onClick={() => setShowInactive(!showInactive)}
                className="gap-2"
                data-testid="toggle-inactive-btn"
              >
                {showInactive ? (
                  <ToggleRight className="w-4 h-4" />
                ) : (
                  <ToggleLeft className="w-4 h-4" />
                )}
                {showInactive ? 'Mostrando todos' : 'Solo activos'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {staff.filter(s => s.active).length}
                  </p>
                  <p className="text-sm text-slate-500">Personal Activo</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <UserCog className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {staff.filter(s => s.cargo === 'Enfermera' || s.cargo === 'Enfermero').length}
                  </p>
                  <p className="text-sm text-slate-500">Enfermeros/as</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <UserCog className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {staff.filter(s => s.cargo === 'Técnico en Enfermería').length}
                  </p>
                  <p className="text-sm text-slate-500">Técnicos</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card data-testid="staff-table-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <UserCog className="w-5 h-5" />
              Listado de Personal ({filteredStaff.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="table-wrapper overflow-x-auto">
              <table className="w-full">
                <thead className="table-header">
                  <tr>
                    <th className="p-4 text-left">Nombre Completo</th>
                    <th className="p-4 text-left">RUT</th>
                    <th className="p-4 text-left">Cargo</th>
                    <th className="p-4 text-center">Estado</th>
                    <th className="p-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="p-8 text-center">
                        <div className="loading-spinner w-8 h-8 mx-auto"></div>
                      </td>
                    </tr>
                  ) : filteredStaff.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-slate-500">
                        <UserCog className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No hay personal registrado</p>
                        <Button 
                          variant="link" 
                          className="mt-2"
                          onClick={() => handleOpenDialog()}
                        >
                          Agregar el primero
                        </Button>
                      </td>
                    </tr>
                  ) : (
                    filteredStaff.map((member) => (
                      <tr key={member.id} className="table-row" data-testid={`staff-row-${member.id}`}>
                        <td className="table-cell font-medium">
                          {member.nombre_completo}
                        </td>
                        <td className="table-cell font-mono text-sm">
                          {member.rut || '-'}
                        </td>
                        <td className="table-cell">
                          <Badge variant="secondary" className="bg-blue-50 text-blue-700">
                            {member.cargo}
                          </Badge>
                        </td>
                        <td className="table-cell text-center">
                          <Badge 
                            className={member.active 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-slate-100 text-slate-500'
                            }
                          >
                            {member.active ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </td>
                        <td className="table-cell text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(member)}
                              data-testid={`edit-btn-${member.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleActive(member)}
                              className={member.active 
                                ? "text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                : "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              }
                              data-testid={`toggle-btn-${member.id}`}
                            >
                              {member.active ? (
                                <ToggleRight className="w-4 h-4" />
                              ) : (
                                <ToggleLeft className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingStaff ? 'Editar Personal' : 'Agregar Personal'}
              </DialogTitle>
              <DialogDescription>
                {editingStaff 
                  ? 'Modifica los datos del personal' 
                  : 'Ingresa los datos del nuevo personal que administra vacunas'
                }
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre_completo">Nombre Completo *</Label>
                  <Input
                    id="nombre_completo"
                    value={formData.nombre_completo}
                    onChange={(e) => setFormData({...formData, nombre_completo: e.target.value})}
                    placeholder="Ej: María González Pérez"
                    data-testid="input-nombre"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rut">RUT</Label>
                  <Input
                    id="rut"
                    value={formData.rut}
                    onChange={(e) => setFormData({...formData, rut: e.target.value})}
                    placeholder="12.345.678-9"
                    data-testid="input-rut"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cargo">Cargo/Profesión *</Label>
                  <Select 
                    value={formData.cargo} 
                    onValueChange={(value) => setFormData({...formData, cargo: value})}
                  >
                    <SelectTrigger data-testid="select-cargo">
                      <SelectValue placeholder="Seleccionar cargo" />
                    </SelectTrigger>
                    <SelectContent>
                      {CARGOS.map(cargo => (
                        <SelectItem key={cargo} value={cargo}>{cargo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleCloseDialog}
                >
                  Cancelar
                </Button>
                <Button type="submit" className="gap-2" data-testid="save-btn">
                  <Save className="w-4 h-4" />
                  {editingStaff ? 'Actualizar' : 'Guardar'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Desactivar personal?</AlertDialogTitle>
              <AlertDialogDescription>
                El personal será desactivado y no aparecerá en las opciones del formulario de vacunación.
                Podrás reactivarlo en cualquier momento.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-delete-btn"
              >
                Desactivar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default StaffPage;
